import React, { useState, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { usePopper } from 'react-popper';

import { useTimeout } from '../../../hooks/useTimeout';
import { Icon, Loading } from '../../UI';
import { SEARCH } from '../../../http/apollo-queries';
import { IStrategy, IAsset } from '../../../types/model';
import { SearchItem } from './SearchItem';

import { logger } from '../../../helpers/logger';

const SHOW_ITEMS_LIMIT = 5;
const FETCH_PREVIEW_ITEMS_LIMIT = 30;
const FETCH_GROUP_ITEMS_LIMIT = 100;

const Search = () => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [viewGroup, setViewGroup] = useState<string>();

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  const toggleDropdown = () => setIsDropdownVisible((isVisible) => !isVisible);

  const searchEmpty = () =>
    search({ variables: { term: '*', limit: FETCH_PREVIEW_ITEMS_LIMIT } });

  const handleClickClose = () => {
    toggleDropdown();
    setValue('');
  };

  const handleClickGroup = (group) => {
    scrollRef.current!.scrollTop = 0;
    setViewGroup(group);
  };

  const handleClickDropdown = () => {
    if (!searchResults || (searchQuery === '' && !value)) {
      searchEmpty();
    }
    toggleDropdown();
    setTimeout(() => document.getElementById('search-input')?.focus(), 0);
  };

  const { value, setValue } = useTimeout(() => {
    setSearchQuery(value || '');

    search({
      variables: {
        term: value || '*',
        limit: viewGroup ? FETCH_GROUP_ITEMS_LIMIT : FETCH_PREVIEW_ITEMS_LIMIT,
      },
    }).then((res) => {
      logger.log('Search:results', res.data?.search?.length);
    });
  }, 1000);

  const [
    search,
    { data: { search: searchResults = [] } = {}, loading, called },
  ] = useLazyQuery<{ search: (IStrategy | IAsset)[] }>(SEARCH, {
    fetchPolicy: 'network-only',
  });

  const popper = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
  });

  useEffect(() => {
    if (popperElement && referenceElement) {
      popperElement.style.top = `-${referenceElement.clientHeight}px`;
    }
  }, [popperElement, referenceElement]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element;

      isDropdownVisible &&
        !target.closest('.modal') &&
        target.tagName !== 'INPUT' &&
        setIsDropdownVisible(false);
    };

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [isDropdownVisible]);

  const renderResults = (searchResults: (IStrategy | IAsset)[]) => {
    return Object.entries(
      // Group results by type or typename
      searchResults.reduce((r, item) => {
        const group =
          searchQuery && item.__typename === 'Asset' && 'type' in item
            ? item.type!
            : item.__typename!;

        r[group] = r[group] || [];
        r[group].push(item);
        return r;
      }, {} as { key: (IStrategy | IAsset)[] })
    ).map(([group, items], groupIndex, groups) => {
      return (
        <div key={'search-group-' + groupIndex}>
          <div className={'text-sm text-grey-30'}>
            {!searchQuery ? t('searchBar.trending') : group}
          </div>
          {items
            .slice(
              0,
              searchQuery ? SHOW_ITEMS_LIMIT : FETCH_PREVIEW_ITEMS_LIMIT
            )
            .map((item) => {
              return (
                <SearchItem
                  item={item}
                  onClick={toggleDropdown}
                  key={'search-item-' + item.id}
                />
              );
            })}

          {searchQuery && items.length > SHOW_ITEMS_LIMIT && (
            <div
              className={`text-sm font-medium text-grey-70 cursor-pointer ${
                groupIndex < groups.length - 1 ? 'pb-4' : ''
              }`}
              onClick={() => handleClickGroup(group)}
            >
              {t('searchBar.allResults')}
            </div>
          )}
        </div>
      );
    });
  };

  const renderGroupResults = (
    searchResults: (IStrategy | IAsset)[],
    group: string
  ) => {
    return (
      <div className={'h-full'} key={'search-group-view-' + group}>
        <div className="border-b border-grey-5 mb-2">
          <div
            className="flex items-center pb-2 py-2 text-sm font-medium text-black cursor-pointer"
            onClick={() => handleClickGroup('')}
          >
            <Icon svgName={'arrowBack'} />
            <span className={'ml-2'}>{t('searchBar.back')}</span>
          </div>
        </div>
        <div className={'text-sm text-grey-30'}>{group}</div>
        {searchResults
          .filter(
            (item) =>
              (item.__typename === 'Strategy' && group === 'Strategy') ||
              ('type' in item && item.type === group)
          )
          .map((item, index) => {
            return (
              <SearchItem
                item={item}
                onClick={toggleDropdown}
                key={'search-item-' + item.id}
              />
            );
          })}
      </div>
    );
  };

  return (
    <>
      <div
        ref={setReferenceElement}
        className="flex items-center pl-5 dark:bg-grey-90 h-full"
        onClick={(e) => e.stopPropagation()}
      >
        <Icon className="h-4 text-lg" color="black" svgName={'search'} />
        <input
          type="text"
          placeholder="Search"
          className="pl-1 text-black dark:bg-grey-90"
          onClick={handleClickDropdown}
        />
      </div>

      <div
        className={`
          dark:bg-grey-90
          dark:border-grey-70
          dark:text-white
          z-90
          w-80
          text-black
          bg-white
          border
          border-grey-10
          rounded-lg
          shadow-xl
          ${!isDropdownVisible ? 'hidden' : ''}
        `}
        style={popper.styles.popper}
        {...popper.attributes.popper}
        ref={setPopperElement}
        id="modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex justify-between m-4 items-center`}>
          <div className="flex">
            <Icon
              className="h-4 text-lg mt-1"
              color="black"
              svgName={'search'}
            />
            <input
              type="text"
              placeholder="Search"
              id="search-input"
              className="pl-1 dark:bg-grey-90"
              autoComplete={'off'}
              value={value || ''}
              onChange={({ target: { value } }) => setValue(value)}
            />
          </div>
          <span onClick={handleClickClose}>
            <Icon
              svgName={'close'}
              className={
                'w-5 h-5 flex justify-center items-center p-1 text-white bg-grey-30 text-xs rounded-full cursor-pointer'
              }
            />
          </span>
        </div>
        <div className={'h-104 p-4 overflow-y-scroll'} ref={scrollRef}>
          {loading ? (
            <Loading className="pt-8 m-auto" status={loading || called} />
          ) : (
            <>
              {!viewGroup
                ? renderResults(searchResults)
                : renderGroupResults(searchResults, viewGroup)}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Search;
